import React, { createContext, useContext, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { MobileNav } from './MobileNav';
import { DesktopSidebar } from './DesktopSidebar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { Navigate } from 'react-router-dom';
import { Loader2, User, Settings, LogOut, ChevronDown, Menu, Copy, Check, QrCode, UserPlus, Shield } from 'lucide-react';
import { useMembershipStatus } from '@/hooks/useMembershipStatus';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useMyInviteCodes, useGenerateInviteCode } from '@/hooks/useInviteCodes';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MobileSidebar } from './MobileSidebar';

interface MembershipContextValue {
  isPending: boolean;
}

const MembershipContext = createContext<MembershipContextValue>({ isPending: false });

export function useMembershipContext() {
  return useContext(MembershipContext);
}

export function AppLayout() {
  const { user, loading, signOut } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { isPending } = useMembershipStatus();
  const { isAdmin } = useIsAdmin();
  const { data: inviteCodes } = useMyInviteCodes({ enabled: !isPending });
  const generateCode = useGenerateInviteCode();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Get first available (unused) invite code
  const availableCode = inviteCodes?.find((c) => !c.is_used);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const inviteUrl = availableCode ? `${baseUrl}/auth?code=${availableCode.code}` : '';

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleCopyCode = async () => {
    if (!availableCode) return;
    await navigator.clipboard.writeText(availableCode.code);
    setCopied(true);
    toast({ title: 'Copied!', description: 'Invite code copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    toast({ title: 'Copied!', description: 'Invite link copied to clipboard' });
  };

  const handleGenerateCode = async () => {
    try {
      await generateCode.mutateAsync();
      toast({ title: 'Code generated!', description: 'Share it with someone you want to invite.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // applied users (no invite code) -> applications page, blocked from app
  if (profile?.membership_status === 'applied') {
    return <Navigate to="/applications" replace />;
  }

  if (profile?.membership_status === 'rejected') {
    return <Navigate to="/auth" replace />;
  }

  // pending users (invited, pre-onboarding) can view but not interact
  // approved users without onboarding should go to onboarding
  if (!profile || (!profile.onboarding_completed && !isPending)) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <MembershipContext.Provider value={{ isPending }}>
      <div className="min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <DesktopSidebar />

        {/* Desktop: Profile dropdown in top right */}
        <div className="hidden lg:flex fixed top-4 right-4 z-50 items-center gap-2">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 p-1.5 rounded-lg bg-card border border-border hover:bg-muted transition-colors outline-none shadow-sm">
              <Avatar className="h-8 w-8">
                <SignedAvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {profile?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{profile?.full_name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{profile?.city_cohort || 'QBN Member'}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                <User className="h-4 w-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {/* Invite Code Section */}
              <div className="px-2 py-2">
                {isPending ? (
                  <p className="text-xs text-muted-foreground">
                    Invite codes unlock after approval.
                  </p>
                ) : availableCode ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Your invite code</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 font-mono font-semibold text-sm bg-muted px-2 py-1 rounded">
                        {availableCode.code}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCopyCode(); }}
                      >
                        {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQrDialogOpen(true); }}
                      >
                        <QrCode className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGenerateCode(); }}
                    disabled={generateCode.isPending}
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    Generate Invite Code
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Mobile Top Bar */}
        <div className="sticky top-0 z-40 flex items-center justify-between px-3 py-2.5 lg:hidden" style={{ background: 'hsl(217 69% 11%)' }}>
          {/* Left: Hamburger Menu */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Center: Logo */}
          <img src="/qbn-logo.png" alt="QBN" className="w-8 h-8 rounded-md" />

          {/* Right: Notifications + Profile */}
          <div className="flex items-center gap-1">
            <NotificationBell iconClassName="h-5 w-5" variant="light" />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 p-1 rounded-lg hover:bg-white/10 transition-colors outline-none">
                <Avatar className="h-7 w-7">
                  <SignedAvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/20 text-white text-xs font-medium">
                    {profile?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="h-3 w-3 text-white/60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{profile?.city_cohort || 'QBN Member'}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {/* Invite Code Section */}
                <div className="px-2 py-2">
                  {availableCode ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Your invite code</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 font-mono font-semibold text-sm bg-muted px-2 py-1 rounded">
                          {availableCode.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCopyCode(); }}
                        >
                          {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQrDialogOpen(true); }}
                        >
                          <QrCode className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGenerateCode(); }}
                      disabled={generateCode.isPending}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                      Generate Invite Code
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Sidebar */}
        <MobileSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main Content */}
        <main className="lg:pl-20 pb-20 lg:pb-0 min-h-screen">
          <div className="max-w-3xl xl:max-w-6xl mx-auto px-4 py-6">
            {isPending && (
              <div className="mb-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3 text-sm text-yellow-600 dark:text-yellow-400">
                Complete your profile to unlock posting, messaging, and full access.
                <a href="/onboarding" className="ml-1 underline font-medium">Complete setup</a>
              </div>
            )}
            <Outlet />
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileNav />

        {/* QR Code Dialog - rendered outside dropdowns to avoid conflicts */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-xs">
            <DialogHeader>
              <DialogTitle className="text-center">Scan to Join QBN</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeSVG value={inviteUrl || 'https://queerbusinessnetwork.com'} size={200} />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm text-muted-foreground">Or share the code</p>
                <code className="font-mono font-bold text-lg">{availableCode?.code}</code>
              </div>
              <div className="flex gap-2 w-full">
                <Button variant="outline" className="flex-1" onClick={handleCopyCode}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Code
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MembershipContext.Provider>
  );
}
